import React, { useState } from 'react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import { useApp } from '../context/AppContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import { useSort, SortableTh } from '../components/SortableTable';
import TableSearch from '../components/TableSearch';

const CATEGORIES = [
  { key: 'mainAssetClass', label: 'Main Asset Class', description: 'Top-level categorisation like Equity, Debt, ETF.', holdingsField: 'mainAssetClass' },
  { key: 'subAssetClass',  label: 'Sub-Asset Class',  description: 'Granular sub-groups: Indian Stocks, Corporate Bond, etc.', holdingsField: 'subAssetClass' },
  { key: 'marketCap',      label: 'Strategy',         description: 'Investment strategy bucket: Focused, Large cap, Mid cap, Multi cap, etc.', holdingsField: 'marketCap' },
  { key: 'geography',      label: 'Geography',        description: 'India, US, Europe, Global, Asia-ex-India, etc.', holdingsField: 'geography' },
  { key: 'holder',         label: 'Holder',           description: 'People or entities holding the assets.', holdingsField: 'holder' },
  { key: 'institution',    label: 'Institution',      description: 'Banks, brokerages, AMCs and other custodians (e.g., HDFC Bank, Zerodha, ICICI Pru AMC).', holdingsField: 'institution' },
];

const List = ({ category }) => {
  const { dropdowns, addDropdownValue, removeDropdownValue, updateDropdownValue, holdings, assets } = useApp();
  const [newVal, setNewVal] = useState('');
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [query, setQuery] = useState('');
  const { sort, onSort, sortRows } = useSort({ key: null, dir: 'asc' });

  // Real usage count per value across holdings + assets
  const usageByValue = React.useMemo(() => {
    const m = {};
    Object.values(holdings).forEach((bySrc) => {
      Object.values(bySrc).forEach((rows) => {
        rows.forEach((r) => {
          const v = r[category.holdingsField];
          if (v) m[v] = (m[v] || 0) + 1;
        });
      });
    });
    // Also count Asset Master entries that reference this value
    if (['mainAssetClass', 'subAssetClass', 'geography'].includes(category.holdingsField)) {
      assets.forEach((a) => {
        const v = a[category.holdingsField];
        if (v) m[v] = (m[v] || 0) + 1;
      });
    }
    return m;
  }, [holdings, assets, category.holdingsField]);

  const values = dropdowns[category.key] || [];
  const rows = values.map((v, i) => ({ value: v, idx: i }));
  const filtered = rows.filter((r) => !query.trim() || r.value.toLowerCase().includes(query.trim().toLowerCase()));
  const sorted = sortRows(filtered, {
    value: (r) => r.value,
    idx: (r) => r.idx,
    usage: (r) => usageByValue[r.value] || 0,
  });

  const add = async () => {
    if (!newVal.trim()) return;
    try {
      await addDropdownValue(category.key, newVal);
      setNewVal('');
      toast.success(`Added "${newVal}" to ${category.label}`);
    } catch (e) {
      toast.error(e.message || 'Could not add');
    }
  };

  const saveEdit = async (old) => {
    if (!editVal.trim() || editVal === old) { setEditing(null); return; }
    try {
      await updateDropdownValue(category.key, old, editVal.trim());
      setEditing(null);
      toast.success(`Renamed "${old}" → "${editVal}"`);
    } catch (e) {
      toast.error(e.message || 'Rename failed');
    }
  };

  const tryDelete = async (v) => {
    const usage = usageByValue[v] || 0;
    if (usage > 0) {
      toast.error(`Cannot delete "${v}" from ${category.label}: in use across ${usage} record${usage === 1 ? '' : 's'}. Reassign those records first.`);
      return;
    }
    if (!confirm(`Delete "${v}" from ${category.label}?`)) return;
    try {
      await removeDropdownValue(category.key, v);
      toast.success(`Removed "${v}"`);
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  return (
    <div className="grid grid-cols-12 gap-8" data-testid={`dd-panel-${category.key}`}>
      <div className="col-span-4">
        <div className="overline mb-2">About this list</div>
        <p className="text-[13px] text-[#575757] leading-relaxed">{category.description}</p>
        <div className="mt-6">
          <div className="overline mb-2">Add new value</div>
          <div className="flex gap-2">
            <Input
              placeholder={`New ${category.label.toLowerCase()}`}
              value={newVal}
              onChange={(e) => setNewVal(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && add()}
              className="h-9 rounded-sm"
              data-testid={`dd-input-${category.key}`}
            />
            <button className="btn-primary" onClick={add} data-testid={`dd-add-${category.key}`}>
              <Plus size={14} />
            </button>
          </div>
          <div className="mt-4 text-[11px] text-[#787872] font-mono">
            {values.length} values · used across all upload & entry flows
          </div>
        </div>
      </div>

      <div className="col-span-8">
        <TableSearch
          value={query}
          onChange={setQuery}
          placeholder={`Search ${category.label.toLowerCase()}…`}
          testId={`dd-search-${category.key}`}
          stats={query ? `${sorted.length} / ${values.length}` : null}
        />
        <div className="border border-border bg-white">
        <table className="grid-table">
          <thead>
            <tr>
              <SortableTh sortKey="idx" sort={sort} onSort={onSort} className="w-[60px]" numeric>#</SortableTh>
              <SortableTh sortKey="value" sort={sort} onSort={onSort}>Value</SortableTh>
              <SortableTh sortKey="usage" sort={sort} onSort={onSort} className="w-[140px]" numeric>Usage</SortableTh>
              <th className="w-[120px]" />
            </tr>
          </thead>
          <tbody>
            {sorted.map(({ value: v, idx: i }) => {
              const usage = usageByValue[v] || 0;
              const inUse = usage > 0;
              const blockedTitle = `Cannot delete "${v}" — in use across ${usage} record${usage === 1 ? '' : 's'}`;
              return (
                <tr key={v}>
                  <td className="num font-mono text-xs text-[#AAA]">{String(i + 1).padStart(2, '0')}</td>
                  <td>
                    {editing === v ? (
                      <Input
                        autoFocus
                        value={editVal}
                        onChange={(e) => setEditVal(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' ? saveEdit(v) : e.key === 'Escape' ? setEditing(null) : null}
                        className="h-7 rounded-sm"
                      />
                    ) : (
                      <span className="font-medium">{v}</span>
                    )}
                  </td>
                  <td className="num">
                    {inUse
                      ? <span className="chip chip-neutral tabular-nums">{usage}</span>
                      : <span className="text-[11px] text-[#AAA] font-mono">unused</span>}
                  </td>
                  <td>
                    <div className="flex justify-end gap-1">
                      {editing === v ? (
                        <>
                          <button className="btn-ghost text-[hsl(var(--positive))]" onClick={() => saveEdit(v)}><Check size={13} /></button>
                          <button className="btn-ghost" onClick={() => setEditing(null)}><X size={13} /></button>
                        </>
                      ) : (
                        <>
                          <button className="btn-ghost" onClick={() => { setEditing(v); setEditVal(v); }} data-testid={`dd-edit-${category.key}-${i}`}>
                            <Pencil size={12} />
                          </button>
                          {inUse ? (
                            <button
                              onClick={() => toast.error(blockedTitle)}
                              className="text-[#CCC] cursor-not-allowed inline-flex p-1.5"
                              title={blockedTitle}
                              data-testid={`dd-delete-${category.key}-${i}-blocked`}
                            >
                              <Trash2 size={12} />
                            </button>
                          ) : (
                            <button
                              className="btn-ghost text-[#9B2C2C] hover:bg-[#F5E4E4]"
                              onClick={() => tryDelete(v)}
                              title={`Delete "${v}"`}
                              data-testid={`dd-delete-${category.key}-${i}`}
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center py-12 text-[#AAA]">No values yet. Add one on the left.</td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

export default function DropdownMaster() {
  return (
    <>
      <PageHeader
        overline="03 · Masters"
        title="Dropdown Master"
        description="Single source of truth for every dropdown in the app. All Excel uploads and in-app forms validate against these lists to prevent data inconsistency."
      />

      <Tabs defaultValue="mainAssetClass">
        <TabsList className="bg-transparent border-b border-border rounded-none w-full justify-start p-0 gap-0 h-auto mb-10">
          {CATEGORIES.map((c) => (
            <TabsTrigger
              key={c.key}
              value={c.key}
              data-testid={`dd-tab-${c.key}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[hsl(var(--primary))] data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[hsl(var(--primary))] text-[13px] font-heading font-semibold text-[#575757] px-4 py-3"
            >
              {c.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {CATEGORIES.map((c) => (
          <TabsContent key={c.key} value={c.key}>
            <List category={c} />
          </TabsContent>
        ))}
      </Tabs>
    </>
  );
}
